/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { createSyncMiddleware, WithSyncControls } from './middleware/syncMiddleware';
import type { 
  ProductionBatch, 
  ProductionLine,
  ProductionMetrics,
  ProductionConstraint,
  ProductionSchedule,
  ManufacturingConstraints,
  WorkInProgress
} from '../types/production';
import type { Factory } from '../types/tycoon';
import type { GameDate } from '../types/events';

interface ProductionError {
  message: string;
  code?: string;
}

interface QualityMetrics {
  productId: string;
  overallQuality: number; // 0-100
  defectRate: number; // 0-1
  yieldRate: number; // 0-1
  customerSatisfaction: number; // 0-100
  qualityTrend: 'improving' | 'stable' | 'declining';
  qualityFactors: {
    materials: number;
    workmanship: number;
    design: number;
    testing: number;
  };
  historicalData: {
    date: GameDate;
    quality: number;
    defects: number;
  }[];
}

interface ProductionCapacity {
  factoryId: string;
  totalCapacity: number;
  usedCapacity: number;
  availableCapacity: number;
  efficiency: number;
  utilizationRate: number;
  productionLines: number;
  activeLines: number;
  maintenanceSchedule: {
    nextMaintenance: GameDate;
    duration: number; // days
    impact: number; // capacity reduction %
  };
}

interface ProductionEfficiency {
  overall: number; // 0-1
  byFactory: Map<string, number>;
  byProduct: Map<string, number>;
  bottlenecks: {
    type: 'labor' | 'equipment' | 'materials' | 'quality';
    severity: number; // 0-1
    location: string;
    impact: number; // production units affected
  }[];
  improvementOpportunities: {
    area: string;
    potentialGain: number;
    investmentRequired: number;
    implementation: string;
  }[];
}

// Helper functions for Map serialization
const mapToObject = <K extends string, V>(map: Map<K, V>): Record<string, V> => {
  const obj: Record<string, V> = {};
  map.forEach((value, key) => {
    obj[key] = value;
  });
  return obj;
};

const objectToMap = <K extends string, V>(obj: Record<string, V>): Map<K, V> => {
  const map = new Map<K, V>();
  Object.entries(obj).forEach(([key, value]) => {
    map.set(key as K, value);
  });
  return map;
};

interface ProductionStore extends WithSyncControls {
  // State
  batches: Map<string, ProductionBatch>;
  productionLines: Map<string, ProductionLine>;
  factories: Map<string, Factory>;
  capacity: Map<string, ProductionCapacity>;
  schedule: Map<string, ProductionSchedule>;
  qualityMetrics: Map<string, QualityMetrics>;
  efficiency: ProductionEfficiency;
  workInProgress: Map<string, WorkInProgress>;
  constraints: ProductionConstraint[];
  
  // Production Status
  status: {
    isLoading: boolean;
    error: ProductionError | null;
    success: boolean;
  };
  
  // Status Management Actions
  setLoadingStatus: (isLoading: boolean) => void;
  setError: (error: ProductionError | null) => void;
  setSuccess: (success: boolean) => void;
  resetStatus: () => void;
  
  // Batch Management
  createBatch: (batch: Omit<ProductionBatch, 'id'>) => string;
  updateBatch: (batchId: string, updates: Partial<ProductionBatch>) => void;
  completeBatch: (batchId: string) => void;
  cancelBatch: (batchId: string) => void;
  
  // Production Line Management
  assignProductToLine: (lineId: string, productId: string, productName: string, gameDate: GameDate) => void;
  updateProductionLine: (lineId: string, updates: Partial<ProductionLine>) => void;
  pauseProductionLine: (lineId: string, reason?: string) => void;
  resumeProductionLine: (lineId: string) => void;
  
  // Capacity Management
  updateCapacity: (factoryId: string, updates: Partial<ProductionCapacity>) => void;
  calculateAvailableCapacity: (factoryId: string) => number;
  allocateCapacity: (factoryId: string, productId: string, units: number) => boolean;
  releaseCapacity: (factoryId: string, units: number) => void;
  
  // Schedule Management
  scheduleProduction: (productId: string, targetUnits: number, startDate: GameDate) => void;
  updateSchedule: (productId: string, updates: Partial<ProductionSchedule>) => void;
  optimizeSchedule: () => void;
  getProductionTimeline: () => { date: GameDate; events: string[] }[];
  
  // Quality Management
  updateQualityMetrics: (productId: string, metrics: Partial<QualityMetrics>) => void;
  performQualityCheck: (batchId: string) => { passed: boolean; issues: string[] };
  improveQuality: (productId: string, investment: number) => void;
  trackQualityTrend: (productId: string) => void;
  
  // Efficiency Management
  calculateEfficiency: () => void;
  identifyBottlenecks: () => void;
  implementEfficiencyImprovement: (improvementId: string) => boolean;
  benchmarkPerformance: () => { metric: string; value: number; industry: number; gap: number }[];
  
  // Factory Management
  addFactory: (factory: Factory) => void;
  updateFactory: (factoryId: string, updates: Partial<Factory>) => void;
  upgradeFactory: (factoryId: string, upgradeType: string) => void;
  scheduleFactoryMaintenance: (factoryId: string, date: GameDate) => void;
  
  // Work In Progress
  updateWorkInProgress: (productId: string, updates: Partial<WorkInProgress>) => void;
  calculateWIPValue: () => number;
  completeWIP: (productId: string, completedUnits: number) => void;
  
  // Constraints & Validation
  checkManufacturingConstraints: (productId: string, quantity: number) => ManufacturingConstraints;
  addConstraint: (constraint: ProductionConstraint) => void;
  resolveConstraint: (constraintType: string, productId: string) => void;
  validateProductionPlan: (plan: { productId?: string; quantity?: number; startDate?: GameDate }) => { valid: boolean; issues: string[] };
  
  // Analytics & Reporting
  getProductionMetrics: (productId: string) => ProductionMetrics | undefined;
  calculateProductionCosts: (productId: string, quantity: number) => { material: number; labor: number; overhead: number; total: number };
  generateProductionReport: (period: 'daily' | 'weekly' | 'monthly') => {
    period: string;
    totalProduction: number;
    activeLines: number;
    utilization: number;
    quality: number;
    efficiency: number;
    topProducts: { productId: string; units: number }[];
    issues: string[];
  };
  forecastProduction: (days: number) => Map<string, number>;
  
  // Selectors
  selectActiveProduction: () => ProductionBatch[];
  selectIdleProductionLines: () => ProductionLine[];
  selectQualityIssues: () => { productId: string; issue: string; severity: number }[];
  selectCapacityUtilization: () => { factoryId: string; utilization: number }[];
  selectUpcomingMaintenance: () => { factoryId: string; date: GameDate }[];
  
  // Complex Operations
  rebalanceProduction: () => void;
  emergencyProduction: (productId: string, quantity: number) => boolean;
  setupNewProduct: (productId: string, factoryId: string) => void;
  decommissionProduct: (productId: string) => void;
  
  // Reset
  resetProduction: () => void;

  // Hydration from save
  hydrateFromSave: (saveData: {
    productionBatches?: Array<{
      id: string;
      productId: string;
      startDate: Date;
      targetUnits: number;
      completedUnits: number;
      qualityScore: number;
      defectRate: number;
      status: 'in-progress' | 'completed';
      costs: { materials: number; labor: number; overhead: number };
    }>;
    factories?: Factory[];
    productionLines?: ProductionLine[];
  } | undefined) => void;
}

const defaultEfficiency: ProductionEfficiency = {
  overall: 0.8,
  byFactory: new Map(),
  byProduct: new Map(),
  bottlenecks: [],
  improvementOpportunities: [],
};

// Partialize function for selective persistence
const partializeProductionStore = (state: ProductionStore) => {
  // Exclude action functions and temporary state
  const {
    // Exclude all action functions
    createBatch: _createBatch,
    updateBatch: _updateBatch,
    completeBatch: _completeBatch,
    cancelBatch: _cancelBatch,
    assignProductToLine: _assignProductToLine,
    updateProductionLine: _updateProductionLine,
    pauseProductionLine: _pauseProductionLine,
    resumeProductionLine: _resumeProductionLine,
    updateCapacity: _updateCapacity,
    calculateAvailableCapacity: _calculateAvailableCapacity,
    allocateCapacity: _allocateCapacity,
    releaseCapacity: _releaseCapacity,
    scheduleProduction: _scheduleProduction,
    updateSchedule: _updateSchedule,
    optimizeSchedule: _optimizeSchedule,
    getProductionTimeline: _getProductionTimeline,
    updateQualityMetrics: _updateQualityMetrics,
    performQualityCheck: _performQualityCheck,
    improveQuality: _improveQuality,
    trackQualityTrend: _trackQualityTrend,
    calculateEfficiency: _calculateEfficiency,
    identifyBottlenecks: _identifyBottlenecks,
    implementEfficiencyImprovement: _implementEfficiencyImprovement,
    benchmarkPerformance: _benchmarkPerformance,
    addFactory: _addFactory,
    updateFactory: _updateFactory,
    upgradeFactory: _upgradeFactory,
    scheduleFactoryMaintenance: _scheduleFactoryMaintenance,
    updateWorkInProgress: _updateWorkInProgress,
    calculateWIPValue: _calculateWIPValue,
    completeWIP: _completeWIP,
    checkManufacturingConstraints: _checkManufacturingConstraints,
    addConstraint: _addConstraint,
    resolveConstraint: _resolveConstraint,
    validateProductionPlan: _validateProductionPlan,
    getProductionMetrics: _getProductionMetrics,
    calculateProductionCosts: _calculateProductionCosts,
    generateProductionReport: _generateProductionReport,
    forecastProduction: _forecastProduction,
    selectActiveProduction: _selectActiveProduction,
    selectIdleProductionLines: _selectIdleProductionLines,
    selectQualityIssues: _selectQualityIssues,
    selectCapacityUtilization: _selectCapacityUtilization,
    selectUpcomingMaintenance: _selectUpcomingMaintenance,
    rebalanceProduction: _rebalanceProduction,
    emergencyProduction: _emergencyProduction,
    setupNewProduct: _setupNewProduct,
    decommissionProduct: _decommissionProduct,
    resetProduction: _resetProduction,
    setLoadingStatus: _setLoadingStatus,
    setError: _setError,
    setSuccess: _setSuccess,
    resetStatus: _resetStatus,
    sync: _sync,
    ...persistableState
  } = state;
  
  // Convert Maps to objects and optimize data
  return {
    batches: mapToObject(persistableState.batches),
    productionLines: mapToObject(persistableState.productionLines),
    factories: mapToObject(persistableState.factories),
    capacity: mapToObject(persistableState.capacity),
    schedule: mapToObject(persistableState.schedule),
    qualityMetrics: mapToObject(persistableState.qualityMetrics),
    efficiency: persistableState.efficiency,
    workInProgress: mapToObject(persistableState.workInProgress),
    constraints: (persistableState.constraints || []).filter(c => c.severity !== 'info'), // Only persist warnings and above
    status: persistableState.status
  };
};

// Sync configuration for production store
const productionSyncConfig = {
  routing: {
    rules: [
      // Batches and factories use IndexedDB with relationships
      {
        match: (path) => 
          path.includes('batches') || 
          path.includes('factories'),
        target: 'indexedDB' as const,
        priority: 100
      },
      // Production lines and schedules use IndexedDB
      {
        match: (path) => 
          path.includes('productionLines') || 
          path.includes('schedule'),
        target: 'indexedDB' as const,
        priority: 90
      },
      // Quality metrics stored separately for performance
      {
        match: (path) => path.includes('qualityMetrics'),
        target: 'indexedDB' as const,
        priority: 85
      },
      // Efficiency and constraints use localStorage
      {
        match: (path) => 
          path.includes('efficiency') || 
          path.includes('constraints'),
        target: 'localStorage' as const,
        priority: 70
      },
      // Work in progress uses both for redundancy
      {
        match: (path) => path.includes('workInProgress'),
        target: 'both' as const,
        priority: 95
      }
    ]
  },
  behavior: {
    debounceDelay: 3000, // 3-second debounce for production updates
    batchSize: 100, // Progressive sync for large batch arrays
    enableAutoSync: true,
    syncOnReconnect: true,
    maxRetries: 5 // More retries for critical production data
  },
  hooks: {
    beforeSync: (data) => {
      // If data is not a full state object (e.g., just a field being synced), return as-is
      if (!data || typeof data !== 'object' || !('batches' in data || 'constraints' in data)) {
        return data;
      }

      // Ensure data integrity
      const partialData = partializeProductionStore(data);

      // Check for quality issues before sync
      if (partialData.qualityMetrics) {
        Object.entries(partialData.qualityMetrics).forEach(([productId, metrics]) => {
          const quality = metrics as QualityMetrics;
          if (quality.defectRate > 0.1 || quality.overallQuality < 60) {
            console.warn('[ProductionStore] Quality alert:',
              `${productId} - Defect rate: ${quality.defectRate}, Quality: ${quality.overallQuality}`
            );
          }
        });
      }

      return partialData;
    },
    afterSync: (result) => {
      if (result.success && result.target === 'indexedDB') {
        console.debug('[ProductionStore] Synced batch-product relationships');
      }
    },
    onError: (error) => {
      console.error('[ProductionStore] Sync error:', error);
      // Sync health error recording is handled by syncMiddleware
    }
  },
  monitoring: {
    trackMetrics: true,
    reportInterval: 45000, // Report every 45 seconds
    metricsCallback: (metrics) => {
      // Metrics are handled by syncMiddleware which has access to syncHealthService
      // console.debug('[ProductionStore] Sync metrics:', {
      //   lastSyncTime: metrics.lastSyncTime,
      //   syncDuration: metrics.averageSyncTime,
      //   pendingChanges: metrics.pendingItems,
      //   failureRate: metrics.failedSyncs / (metrics.totalSyncs || 1)
      // });
    }
  }
};

export const useProductionStore = create<ProductionStore>()(
  devtools(
    createSyncMiddleware(
      'productionStore',
      productionSyncConfig
    )(
      (set, get) => ({
      // Initial state
      batches: new Map(),
      productionLines: new Map(),
      factories: new Map(),
      capacity: new Map(),
      schedule: new Map(),
      qualityMetrics: new Map(),
      efficiency: defaultEfficiency,
      workInProgress: new Map(),
      constraints: [],
      status: {
        isLoading: false,
        error: null,
        success: false,
      },

      // Status Management Actions
      setLoadingStatus: (isLoading) =>
        set(
          (state) => ({
            status: { ...state.status, isLoading },
          }),
          false,
          'setLoadingStatus'
        ),

      setError: (error) =>
        set(
          (state) => ({
            status: { ...state.status, error, success: false },
          }),
          false,
          'setError'
        ),

      setSuccess: (success) =>
        set(
          (state) => ({
            status: { ...state.status, success, error: null },
          }),
          false,
          'setSuccess'
        ),

      resetStatus: () =>
        set(
          {
            status: {
              isLoading: false,
              error: null,
              success: false,
            },
          },
          false,
          'resetStatus'
        ),

      // Batch Management
      createBatch: (batch) => {
        const batchId = `BATCH-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        set(
          (state) => {
            const batches = new Map(state.batches);
            batches.set(batchId, { ...batch, id: batchId });
            return { batches };
          },
          false,
          'createBatch'
        );
        return batchId;
      },

      updateBatch: (batchId, updates) =>
        set(
          (state) => {
            const batches = new Map(state.batches);
            const existing = batches.get(batchId);
            if (existing) {
              batches.set(batchId, { ...existing, ...updates });
            }
            return { batches };
          },
          false,
          'updateBatch'
        ),

      completeBatch: (batchId) => {
        const state = get();
        const batch = state.batches.get(batchId);
        
        if (batch) {
          state.updateBatch(batchId, {
            status: 'completed',
            completedUnits: batch.targetUnits,
          });
          
          // Update WIP
          const wip = state.workInProgress.get(batch.productId);
          if (wip) {
            state.completeWIP(batch.productId, batch.targetUnits);
          }
        }
      },

      cancelBatch: (batchId) =>
        set(
          (state) => {
            const batches = new Map(state.batches);
            const batch = batches.get(batchId);
            if (batch) {
              batches.set(batchId, { ...batch, status: 'cancelled' });
            }
            return { batches };
          },
          false,
          'cancelBatch'
        ),

      // Production Line Management
      assignProductToLine: (lineId, productId, productName, gameDate) =>
        set(
          (state) => {
            const productionLines = new Map(state.productionLines);
            const line = productionLines.get(lineId);
            if (line) {
              productionLines.set(lineId, {
                ...line,
                productId,
                productName,
                status: 'active',
                pauseReason: undefined,
                lastProductChange: gameDate,
              });
            }
            return { productionLines };
          },
          false,
          'assignProductToLine'
        ),

      updateProductionLine: (lineId, updates) =>
        set(
          (state) => {
            const productionLines = new Map(state.productionLines);
            const existing = productionLines.get(lineId);
            if (existing) {
              productionLines.set(lineId, { ...existing, ...updates });
            } else {
              // If line doesn't exist, create it with the updates
              productionLines.set(lineId, updates as ProductionLine);
            }
            return { productionLines };
          },
          false,
          'updateProductionLine'
        ),

      pauseProductionLine: (lineId, reason) =>
        set(
          (state) => {
            const productionLines = new Map(state.productionLines);
            const line = productionLines.get(lineId);
            if (line) {
              productionLines.set(lineId, {
                ...line,
                status: 'paused',
                pauseReason: reason as any,
              });
            }
            return { productionLines };
          },
          false,
          'pauseProductionLine'
        ),

      resumeProductionLine: (lineId) =>
        set(
          (state) => {
            const productionLines = new Map(state.productionLines);
            const line = productionLines.get(lineId);
            if (line && line.productId && line.allocatedEmployees > 0) {
              productionLines.set(lineId, {
                ...line,
                status: 'active',
                pauseReason: undefined,
              });
            }
            return { productionLines };
          },
          false,
          'resumeProductionLine'
        ),

      // Capacity Management
      updateCapacity: (factoryId, updates) =>
        set(
          (state) => {
            const capacity = new Map(state.capacity);
            const existing = capacity.get(factoryId);
            if (existing) {
              const updated = { ...existing, ...updates };
              updated.availableCapacity = updated.totalCapacity - updated.usedCapacity;
              updated.utilizationRate = updated.totalCapacity > 0 
                ? updated.usedCapacity / updated.totalCapacity 
                : 0;
              capacity.set(factoryId, updated);
            }
            return { capacity };
          },
          false,
          'updateCapacity'
        ),

      calculateAvailableCapacity: (factoryId) => {
        const state = get();
        const cap = state.capacity.get(factoryId);
        return cap ? cap.availableCapacity : 0;
      },

      allocateCapacity: (factoryId, productId, units) => {
        const state = get();
        const cap = state.capacity.get(factoryId);
        
        if (!cap || cap.availableCapacity < units) {
          return false;
        }
        
        state.updateCapacity(factoryId, {
          usedCapacity: cap.usedCapacity + units,
        });
        
        return true;
      },

      releaseCapacity: (factoryId, units) => {
        const state = get();
        const cap = state.capacity.get(factoryId);
        
        if (cap) {
          state.updateCapacity(factoryId, {
            usedCapacity: Math.max(0, cap.usedCapacity - units),
          });
        }
      },

      // Schedule Management
      scheduleProduction: (productId, targetUnits) =>
        set(
          (state) => {
            const schedule = new Map(state.schedule);
            schedule.set(productId, {
              productId,
              targetUnitsPerDay: targetUnits,
              actualUnitsPerDay: 0,
              componentRequirements: new Map(),
              employeeRequirements: Math.ceil(targetUnits / 50), // Simplified calculation
              constraints: [],
            });
            return { schedule };
          },
          false,
          'scheduleProduction'
        ),

      updateSchedule: (productId, updates) =>
        set(
          (state) => {
            const schedule = new Map(state.schedule);
            const existing = schedule.get(productId);
            if (existing) {
              schedule.set(productId, { ...existing, ...updates });
            }
            return { schedule };
          },
          false,
          'updateSchedule'
        ),

      optimizeSchedule: () => {
        const state = get();
        
        // Rebalance production based on demand and capacity
        state.schedule.forEach((sched, productId) => {
          const constraints = state.checkManufacturingConstraints(productId, sched.targetUnitsPerDay);
          
          if (!constraints.canProduce) {
            // Reduce target if constraints exist
            const newTarget = Math.floor(sched.targetUnitsPerDay * 0.8);
            state.updateSchedule(productId, {
              targetUnitsPerDay: newTarget,
              constraints: [{
                type: 'capacity_limit',
                affectedProducts: [productId],
                severity: 'warning',
                message: constraints.message || 'Production constraints detected',
                estimatedImpact: sched.targetUnitsPerDay - newTarget,
              }],
            });
          }
        });
      },

      getProductionTimeline: () => {
        const state = get();
        const timeline: { date: GameDate; events: string[] }[] = [];
        
        // Add batch completions
        state.batches.forEach(batch => {
          if (batch.status === 'in-progress') {
            // Simplified - would calculate actual completion date
            timeline.push({
              date: { day: 1, month: 1, year: 2007 },
              events: [`Batch ${batch.id} completion`],
            });
          }
        });
        
        // Add maintenance schedules
        state.capacity.forEach((cap, factoryId) => {
          if (cap.maintenanceSchedule) {
            timeline.push({
              date: cap.maintenanceSchedule.nextMaintenance,
              events: [`Factory ${factoryId} maintenance`],
            });
          }
        });
        
        return timeline.sort((a, b) => {
          // Sort by date (simplified comparison)
          return a.date.year - b.date.year || a.date.month - b.date.month || a.date.day - b.date.day;
        });
      },

      // Quality Management
      updateQualityMetrics: (productId, metrics) =>
        set(
          (state) => {
            const qualityMetrics = new Map(state.qualityMetrics);
            const existing = qualityMetrics.get(productId);
            if (existing) {
              qualityMetrics.set(productId, { ...existing, ...metrics });
            } else {
              qualityMetrics.set(productId, {
                productId,
                overallQuality: 80,
                defectRate: 0.02,
                yieldRate: 0.98,
                customerSatisfaction: 85,
                qualityTrend: 'stable',
                qualityFactors: {
                  materials: 0.85,
                  workmanship: 0.8,
                  design: 0.9,
                  testing: 0.85,
                },
                historicalData: [],
                ...metrics,
              });
            }
            return { qualityMetrics };
          },
          false,
          'updateQualityMetrics'
        ),

      performQualityCheck: (batchId) => {
        const state = get();
        const batch = state.batches.get(batchId);
        
        if (!batch) {
          return { passed: false, issues: ['Batch not found'] };
        }
        
        const quality = state.qualityMetrics.get(batch.productId);
        if (!quality) {
          return { passed: true, issues: [] };
        }
        
        const issues: string[] = [];
        let passed = true;
        
        if (quality.defectRate > 0.05) {
          issues.push('High defect rate detected');
          passed = false;
        }
        
        if (quality.overallQuality < 70) {
          issues.push('Quality below acceptable threshold');
          passed = false;
        }
        
        if (batch.defectRate > 0.1) {
          issues.push('Batch defect rate exceeds limit');
          passed = false;
        }
        
        return { passed, issues };
      },

      improveQuality: (productId, investment) => {
        const state = get();
        const quality = state.qualityMetrics.get(productId);
        
        if (quality) {
          // Simple improvement calculation
          const improvementFactor = Math.min(0.1, investment / 100000);
          
          state.updateQualityMetrics(productId, {
            overallQuality: Math.min(100, quality.overallQuality + improvementFactor * 20),
            defectRate: Math.max(0, quality.defectRate - improvementFactor * 0.5),
            yieldRate: Math.min(1, quality.yieldRate + improvementFactor * 0.1),
            qualityTrend: 'improving',
          });
        }
      },

      trackQualityTrend: (productId) => {
        const state = get();
        const quality = state.qualityMetrics.get(productId);
        
        if (quality && quality.historicalData.length > 2) {
          const recent = quality.historicalData.slice(-3);
          const avgRecent = recent.reduce((sum, d) => sum + d.quality, 0) / recent.length;
          const avgPrevious = quality.historicalData.slice(-6, -3).reduce((sum, d) => sum + d.quality, 0) / 3;
          
          const trend = avgRecent > avgPrevious ? 'improving' : avgRecent < avgPrevious ? 'declining' : 'stable';
          
          state.updateQualityMetrics(productId, { qualityTrend: trend });
        }
      },

      // Efficiency Management
      calculateEfficiency: () => {
        const state = get();
        const byFactory = new Map<string, number>();
        const byProduct = new Map<string, number>();
        
        // Calculate factory efficiency
        state.capacity.forEach((cap, factoryId) => {
          byFactory.set(factoryId, cap.efficiency * cap.utilizationRate);
        });
        
        // Calculate product efficiency
        state.productionLines.forEach(line => {
          if (line.productId) {
            const current = byProduct.get(line.productId) || 0;
            byProduct.set(line.productId, Math.max(current, line.efficiency));
          }
        });
        
        // Calculate overall efficiency
        const efficiencyValues = Array.from(byFactory.values());
        const overall = efficiencyValues.length > 0
          ? efficiencyValues.reduce((sum, e) => sum + e, 0) / efficiencyValues.length
          : 0.8;
        
        set(
          {
            efficiency: {
              overall,
              byFactory,
              byProduct,
              bottlenecks: state.efficiency.bottlenecks,
              improvementOpportunities: state.efficiency.improvementOpportunities,
            },
          },
          false,
          'calculateEfficiency'
        );
      },

      identifyBottlenecks: () => {
        const state = get();
        const bottlenecks: typeof state.efficiency.bottlenecks = [];
        
        // Check labor bottlenecks
        state.productionLines.forEach(line => {
          if (line.status === 'paused' && line.pauseReason === 'no_employees') {
            bottlenecks.push({
              type: 'labor',
              severity: 0.8,
              location: `Line ${line.lineNumber}`,
              impact: line.dailyOutput,
            });
          }
        });
        
        // Check material bottlenecks
        state.constraints.forEach(constraint => {
          if (constraint.type === 'component_shortage') {
            bottlenecks.push({
              type: 'materials',
              severity: constraint.severity === 'critical' ? 0.9 : 0.5,
              location: 'Supply chain',
              impact: constraint.estimatedImpact,
            });
          }
        });
        
        // Check equipment bottlenecks
        state.capacity.forEach((cap, factoryId) => {
          if (cap.utilizationRate > 0.95) {
            bottlenecks.push({
              type: 'equipment',
              severity: 0.7,
              location: `Factory ${factoryId}`,
              impact: cap.totalCapacity * 0.1,
            });
          }
        });
        
        set(
          (state) => ({
            efficiency: {
              ...state.efficiency,
              bottlenecks,
            },
          }),
          false,
          'identifyBottlenecks'
        );
      },

      implementEfficiencyImprovement: (improvementId) => {
        // Simplified implementation
        const state = get();
        const improvement = state.efficiency.improvementOpportunities.find(i => i.area === improvementId);
        
        if (improvement) {
          state.calculateEfficiency();
          return true;
        }
        
        return false;
      },

      benchmarkPerformance: () => {
        const state = get();
        const benchmarks: { metric: string; value: number; industry: number; gap: number }[] = [];
        
        // Efficiency benchmark
        benchmarks.push({
          metric: 'Overall Efficiency',
          value: state.efficiency.overall,
          industry: 0.85,
          gap: 0.85 - state.efficiency.overall,
        });
        
        // Quality benchmark
        const avgQuality = state.qualityMetrics.size > 0
          ? Array.from(state.qualityMetrics.values()).reduce((sum, q) => sum + q.overallQuality, 0) / state.qualityMetrics.size
          : 0;
        benchmarks.push({
          metric: 'Average Quality',
          value: avgQuality / 100,
          industry: 0.9,
          gap: 0.9 - avgQuality / 100,
        });
        
        // Utilization benchmark
        const avgUtilization = state.capacity.size > 0
          ? Array.from(state.capacity.values()).reduce((sum, c) => sum + c.utilizationRate, 0) / state.capacity.size
          : 0;
        benchmarks.push({
          metric: 'Capacity Utilization',
          value: avgUtilization,
          industry: 0.75,
          gap: 0.75 - avgUtilization,
        });
        
        return benchmarks;
      },

      // Factory Management
      addFactory: (factory) =>
        set(
          (state) => {
            // Only add if not already present
            if (state.factories.has(factory.id)) {
              return state;
            }
            
            const factories = new Map(state.factories);
            const capacity = new Map(state.capacity);
            
            factories.set(factory.id, factory);
            
            // Initialize capacity for new factory
            // Use productionCapacity from the factory object (production units per day)
            // Not the worker capacity which is stored in factory.capacity
            const productionCapacity = factory.productionCapacity || 100; // Default 100 units/day if not specified
            
            capacity.set(factory.id, {
              factoryId: factory.id,
              totalCapacity: productionCapacity,
              usedCapacity: 0,
              availableCapacity: productionCapacity,
              efficiency: factory.efficiency,
              utilizationRate: 0,
              productionLines: factory.productionLines || 3,
              activeLines: 0,
              maintenanceSchedule: {
                nextMaintenance: { day: 1, month: 1, year: 2008 },
                duration: 2,
                impact: 0.5,
              },
            });
            
            return { factories, capacity };
          },
          false,
          'addFactory'
        ),

      updateFactory: (factoryId, updates) =>
        set(
          (state) => {
            const factories = new Map(state.factories);
            const existing = factories.get(factoryId);
            if (existing) {
              factories.set(factoryId, { ...existing, ...updates });
            }
            return { factories };
          },
          false,
          'updateFactory'
        ),

      upgradeFactory: (factoryId, upgradeType) => {
        const state = get();
        const factory = state.factories.get(factoryId);
        
        if (factory) {
          const upgrades: Record<string, Partial<Factory>> = {
            capacity: { capacity: factory.capacity * 1.5 },
            efficiency: { efficiency: Math.min(1, factory.efficiency + 0.1) },
            automation: { operatingCost: factory.operatingCost * 0.8 },
          };
          
          const upgrade = upgrades[upgradeType];
          if (upgrade) {
            state.updateFactory(factoryId, upgrade);
            
            // Update capacity if needed
            if (upgradeType === 'capacity') {
              state.updateCapacity(factoryId, {
                totalCapacity: factory.capacity * 1.5,
              });
            }
          }
        }
      },

      scheduleFactoryMaintenance: (factoryId, date) =>
        set(
          (state) => {
            const capacity = new Map(state.capacity);
            const cap = capacity.get(factoryId);
            if (cap) {
              capacity.set(factoryId, {
                ...cap,
                maintenanceSchedule: {
                  ...cap.maintenanceSchedule,
                  nextMaintenance: date,
                },
              });
            }
            return { capacity };
          },
          false,
          'scheduleFactoryMaintenance'
        ),

      // Work In Progress
      updateWorkInProgress: (productId, updates) =>
        set(
          (state) => {
            const workInProgress = new Map(state.workInProgress);
            const existing = workInProgress.get(productId);
            if (existing) {
              workInProgress.set(productId, { ...existing, ...updates });
            } else {
              workInProgress.set(productId, {
                productId,
                productName: '',
                quantity: 0,
                startDate: { day: 1, month: 1, year: 2007 },
                expectedCompletion: { day: 1, month: 1, year: 2007 },
                progress: 0,
                componentsValue: 0,
                laborCost: 0,
                totalValue: 0,
                ...updates,
              });
            }
            return { workInProgress };
          },
          false,
          'updateWorkInProgress'
        ),

      calculateWIPValue: () => {
        const state = get();
        return Array.from(state.workInProgress.values())
          .reduce((sum, wip) => sum + wip.totalValue, 0);
      },

      completeWIP: (productId, completedUnits) => {
        const state = get();
        const wip = state.workInProgress.get(productId);
        
        if (wip) {
          const completionRatio = completedUnits / wip.quantity;
          const remainingQuantity = wip.quantity - completedUnits;
          
          if (remainingQuantity <= 0) {
            // Remove from WIP if fully completed
            const workInProgress = new Map(state.workInProgress);
            workInProgress.delete(productId);
            set({ workInProgress }, false, 'completeWIP');
          } else {
            // Update remaining WIP
            state.updateWorkInProgress(productId, {
              quantity: remainingQuantity,
              componentsValue: wip.componentsValue * (1 - completionRatio),
              laborCost: wip.laborCost * (1 - completionRatio),
              totalValue: wip.totalValue * (1 - completionRatio),
            });
          }
        }
      },

      // Constraints & Validation
      checkManufacturingConstraints: (productId, quantity) => {
        const state = get();
        const constraints: ManufacturingConstraints = {
          canProduce: true,
          hasComponents: true,
          hasWorkers: true,
          hasCapacity: true,
          hasProductionLine: false,
          message: '',
        };
        
        // When assigning a product to a line, we don't need to check production capacity
        // The actual capacity will be determined by workers assigned to the line
        // This check should only validate if a line exists, not production volume
        
        // Check if any production line exists (can be idle or have any product - lockout is validated separately)
        const hasAvailableLine = Array.from(state.productionLines.values())
          .some(line => line.factoryBuildingId);
        
        if (!hasAvailableLine) {
          constraints.canProduce = false;
          constraints.hasProductionLine = false;
          constraints.message = 'No production lines available';
        } else {
          constraints.hasProductionLine = true;
        }
        
        // Don't check capacity when just assigning a product
        // Capacity should be checked when actually producing, not when setting up
        constraints.hasCapacity = true;
        
        return constraints;
      },

      addConstraint: (constraint) =>
        set(
          (state) => ({
            constraints: [...state.constraints, constraint],
          }),
          false,
          'addConstraint'
        ),

      resolveConstraint: (constraintType, productId) =>
        set(
          (state) => ({
            constraints: state.constraints.filter(c => 
              !(c.type === constraintType && c.affectedProducts.includes(productId))
            ),
          }),
          false,
          'resolveConstraint'
        ),

      validateProductionPlan: (plan) => {
        const issues: string[] = [];
        let valid = true;
        
        // Basic validation
        if (!plan.productId) {
          issues.push('Product ID is required');
          valid = false;
        }
        
        if (!plan.quantity || plan.quantity <= 0) {
          issues.push('Valid quantity is required');
          valid = false;
        }
        
        if (!plan.startDate) {
          issues.push('Start date is required');
          valid = false;
        }
        
        return { valid, issues };
      },

      // Analytics & Reporting
      getProductionMetrics: (productId) => {
        const state = get();
        
        // Calculate metrics from batches and lines
        const productBatches = Array.from(state.batches.values())
          .filter(b => b.productId === productId);
        
        const productLines = Array.from(state.productionLines.values())
          .filter(l => l.productId === productId);
        
        if (productBatches.length === 0 && productLines.length === 0) {
          return undefined;
        }
        
        const totalProduced = productBatches
          .filter(b => b.status === 'completed')
          .reduce((sum, b) => sum + b.completedUnits, 0);
        
        const dailyProduction = productLines
          .reduce((sum, l) => sum + l.dailyOutput, 0);
        
        const quality = state.qualityMetrics.get(productId);
        
        const metrics: ProductionMetrics = {
          productId,
          dailyProduction,
          monthlyProduction: dailyProduction * 30,
          totalProduced,
          averageQuality: quality?.overallQuality || 85,
          defectRate: quality?.defectRate || 0.02,
          efficiency: productLines.length > 0
            ? productLines.reduce((sum, l) => sum + l.efficiency, 0) / productLines.length
            : 0.8,
          componentCostPerUnit: 0, // Would need component data
          laborCostPerUnit: 0, // Would need employee data
          totalCostPerUnit: 0, // Sum of all costs
        };
        
        return metrics;
      },

      calculateProductionCosts: (productId, quantity) => {
        // Simplified cost calculation
        const baseMaterialCost = 50;
        const baseLaborCost = 20;
        const baseOverheadCost = 10;
        
        return {
          material: baseMaterialCost * quantity,
          labor: baseLaborCost * quantity,
          overhead: baseOverheadCost * quantity,
          total: (baseMaterialCost + baseLaborCost + baseOverheadCost) * quantity,
        };
      },

      generateProductionReport: (period) => {
        const state = get();
        
        const report = {
          period,
          totalProduction: 0,
          activeLines: 0,
          utilization: 0,
          quality: 0,
          efficiency: state.efficiency.overall,
          topProducts: [] as { productId: string; units: number }[],
          issues: [] as string[],
        };
        
        // Calculate totals
        state.productionLines.forEach(line => {
          if (line.status === 'active') {
            report.activeLines++;
            report.totalProduction += line.dailyOutput * (period === 'daily' ? 1 : period === 'weekly' ? 7 : 30);
          }
        });
        
        // Calculate average utilization
        const utilizationValues = Array.from(state.capacity.values()).map(c => c.utilizationRate);
        report.utilization = utilizationValues.length > 0
          ? utilizationValues.reduce((sum, u) => sum + u, 0) / utilizationValues.length
          : 0;
        
        // Calculate average quality
        const qualityValues = Array.from(state.qualityMetrics.values()).map(q => q.overallQuality);
        report.quality = qualityValues.length > 0
          ? qualityValues.reduce((sum, q) => sum + q, 0) / qualityValues.length
          : 0;
        
        // Identify issues
        state.constraints.forEach(constraint => {
          if (constraint.severity === 'critical') {
            report.issues.push(constraint.message);
          }
        });
        
        return report;
      },

      forecastProduction: (days) => {
        const state = get();
        const forecast = new Map<string, number>();
        
        state.productionLines.forEach(line => {
          if (line.productId && line.status === 'active') {
            const current = forecast.get(line.productId) || 0;
            forecast.set(line.productId, current + line.dailyOutput * days);
          }
        });
        
        return forecast;
      },

      // Selectors
      selectActiveProduction: () => {
        const state = get();
        return Array.from(state.batches.values())
          .filter(b => b.status === 'in-progress');
      },

      selectIdleProductionLines: () => {
        const state = get();
        return Array.from(state.productionLines.values())
          .filter(l => l.status === 'idle' || !l.productId);
      },

      selectQualityIssues: () => {
        const state = get();
        const issues: { productId: string; issue: string; severity: number }[] = [];
        
        state.qualityMetrics.forEach((quality, productId) => {
          if (quality.defectRate > 0.05) {
            issues.push({
              productId,
              issue: 'High defect rate',
              severity: quality.defectRate,
            });
          }
          
          if (quality.overallQuality < 70) {
            issues.push({
              productId,
              issue: 'Low quality score',
              severity: (100 - quality.overallQuality) / 100,
            });
          }
          
          if (quality.qualityTrend === 'declining') {
            issues.push({
              productId,
              issue: 'Declining quality trend',
              severity: 0.5,
            });
          }
        });
        
        return issues.sort((a, b) => b.severity - a.severity);
      },

      selectCapacityUtilization: () => {
        const state = get();
        return Array.from(state.capacity.entries())
          .map(([factoryId, cap]) => ({
            factoryId,
            utilization: cap.utilizationRate,
          }))
          .sort((a, b) => b.utilization - a.utilization);
      },

      selectUpcomingMaintenance: () => {
        const state = get();
        const upcoming: { factoryId: string; date: GameDate }[] = [];
        
        state.capacity.forEach((cap, factoryId) => {
          if (cap.maintenanceSchedule) {
            upcoming.push({
              factoryId,
              date: cap.maintenanceSchedule.nextMaintenance,
            });
          }
        });
        
        return upcoming;
      },

      // Complex Operations
      rebalanceProduction: () => {
        const state = get();
        
        // Find underutilized lines
        const idleLines = state.selectIdleProductionLines();
        
        // Find overloaded products
        const productLoads = new Map<string, number>();
        state.productionLines.forEach(line => {
          if (line.productId) {
            const current = productLoads.get(line.productId) || 0;
            productLoads.set(line.productId, current + 1);
          }
        });
        
        // Reassign idle lines to overloaded products
        idleLines.forEach(line => {
          let maxLoad = 0;
          let targetProduct = '';
          
          productLoads.forEach((load, productId) => {
            if (load > maxLoad) {
              maxLoad = load;
              targetProduct = productId;
            }
          });
          
          if (targetProduct && maxLoad > 2) {
            state.assignProductToLine(line.id, targetProduct);
          }
        });
      },

      emergencyProduction: (productId, quantity) => {
        const state = get();
        
        // Find available capacity across all factories
        let remainingQuantity = quantity;
        let allocated = false;
        
        state.capacity.forEach((cap, factoryId) => {
          if (remainingQuantity > 0 && cap.availableCapacity > 0) {
            const allocate = Math.min(remainingQuantity, cap.availableCapacity);
            
            if (state.allocateCapacity(factoryId, productId, allocate)) {
              remainingQuantity -= allocate;
              allocated = true;
              
              // Create emergency batch
              state.createBatch({
                productId,
                startDate: new Date(),
                targetUnits: allocate,
                completedUnits: 0,
                qualityScore: 0.9, // Slightly lower for rush job
                defectRate: 0.05, // Slightly higher for rush job
                status: 'in-progress',
                costs: {
                  materials: allocate * 60, // Premium for rush
                  labor: allocate * 30, // Overtime costs
                  overhead: allocate * 15,
                },
              });
            }
          }
        });
        
        return allocated && remainingQuantity === 0;
      },

      setupNewProduct: (productId, factoryId) => {
        const state = get();
        
        // Find available production line
        const lines = Array.from(state.productionLines.values())
          .filter(l => l.factoryBuildingId === factoryId && (l.status === 'idle' || !l.productId));
        
        if (lines.length > 0) {
          state.assignProductToLine(lines[0].id, productId);
          
          // Initialize quality metrics
          state.updateQualityMetrics(productId, {
            productId,
            overallQuality: 85,
            defectRate: 0.03,
            yieldRate: 0.97,
            customerSatisfaction: 80,
            qualityTrend: 'stable',
          });
          
          // Create initial production schedule
          state.scheduleProduction(productId, 100, { day: 1, month: 1, year: 2007 });
        }
      },

      decommissionProduct: (productId) => {
        const state = get();
        
        // Pause all production lines for this product
        state.productionLines.forEach((line, lineId) => {
          if (line.productId === productId) {
            state.pauseProductionLine(lineId, 'no_product');
            state.updateProductionLine(lineId, { productId: null });
          }
        });
        
        // Cancel active batches
        state.batches.forEach((batch, batchId) => {
          if (batch.productId === productId && batch.status === 'in-progress') {
            state.cancelBatch(batchId);
          }
        });
        
        // Remove from schedule
        const schedule = new Map(state.schedule);
        schedule.delete(productId);
        set({ schedule }, false, 'decommissionProduct');
      },

      // Reset
      resetProduction: () =>
        set(
          {
            batches: new Map(),
            productionLines: new Map(),
            factories: new Map(),
            capacity: new Map(),
            schedule: new Map(),
            qualityMetrics: new Map(),
            efficiency: defaultEfficiency,
            workInProgress: new Map(),
            constraints: [],
            status: {
              isLoading: false,
              error: null,
              success: false,
            },
          },
          false,
          'resetProduction'
        ),

      // Hydration from save
      hydrateFromSave: (saveData) =>
        set(
          (state) => {
            if (!saveData) return state;

            // Convert production batches array to Map
            const batches = new Map<string, ProductionBatch>();
            (saveData.productionBatches ?? []).forEach(batch => {
              batches.set(batch.id, batch as ProductionBatch);
            });

            // Convert factories array to Map and initialize capacity
            const factories = new Map<string, Factory>();
            const capacity = new Map<string, ProductionCapacity>();
            (saveData.factories ?? []).forEach(factory => {
              factories.set(factory.id, factory);
              // Initialize capacity for each factory
              capacity.set(factory.id, {
                factoryId: factory.id,
                totalCapacity: factory.productionCapacity || 1000,
                usedCapacity: 0,
                availableCapacity: factory.productionCapacity || 1000,
                efficiency: factory.efficiency || 0.85,
                utilizationRate: 0,
                productionLines: factory.productionLines || 3,
                activeLines: 0,
                maintenanceSchedule: {
                  nextMaintenance: { day: 1, month: 1, year: 2008 },
                  duration: 7,
                  impact: 0.5,
                },
              });
            });

            // Convert production lines array to Map
            const productionLines = new Map<string, ProductionLine>();
            (saveData.productionLines ?? []).forEach(line => {
              productionLines.set(line.id, line);
            });

            return {
              batches,
              factories,
              capacity,
              // Restore production lines from save (or keep existing if not provided)
              productionLines: saveData.productionLines ? productionLines : state.productionLines,
              schedule: state.schedule,
              qualityMetrics: state.qualityMetrics,
              workInProgress: state.workInProgress,
              constraints: [],
              status: { isLoading: false, error: null, success: false },
            };
          },
          false,
          'hydrateFromSave'
        ),
      }),
    ),
    {
      name: 'production-store',
    }
  )
);

// Selectors
export const selectBatches = (state: ProductionStore) => state.batches;
export const selectProductionLines = (state: ProductionStore) => state.productionLines;
export const selectFactories = (state: ProductionStore) => state.factories;
export const selectCapacity = (state: ProductionStore) => state.capacity;
export const selectSchedule = (state: ProductionStore) => state.schedule;
export const selectQualityMetrics = (state: ProductionStore) => state.qualityMetrics;
export const selectEfficiency = (state: ProductionStore) => state.efficiency;
export const selectWorkInProgress = (state: ProductionStore) => state.workInProgress;
export const selectConstraints = (state: ProductionStore) => state.constraints;
export const selectProductionStatus = (state: ProductionStore) => state.status;